import React, { useState, useEffect } from 'react';
import { Check, CircleCheckBig, CircleX, RefreshCw, TriangleAlert } from 'lucide-react';
import { selectiveApi } from '../../services/selectiveApi';
import './AIConfigPanel.css';

/**
 * AIConfigPanel Component
 *
 * Admin interface for configuring AI model settings for Selective Exam question generation.
 * Primary: Claude Sonnet 5. Fallback: Gemini Pro. AUTO selects Claude when available.
 */
const AIConfigPanel = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [settings, setSettings] = useState({
    ai_provider: 'auto',
    gemini_model: 'gemini-pro-latest',
    claude_model: 'claude-sonnet-5',
    use_exemplars: true,
    min_distractor_quality: 0.6,
    enable_quality_validation: true,
    fallback_enabled: true,
    max_questions_per_request: 20,
    generation_timeout_seconds: 120,
  });

  // Provider status state
  const [providerStatus, setProviderStatus] = useState({
    gemini: { available: false, model: '', error: null },
    claude: { available: false, model: '', error: null },
    preferred_provider: 'auto'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);

    try {
      const [settingsData, statusData] = await Promise.all([
        selectiveApi.admin.getAISettings(),
        selectiveApi.admin.getAIProviderStatus()
      ]);

      setSettings(settingsData);

      // Ensure statusData has the expected structure
      if (statusData && statusData.gemini && statusData.claude) {
        setProviderStatus(statusData);
      } else {
        console.warn('Invalid provider status structure:', statusData);
        // Keep default state if API returns unexpected structure
      }
    } catch (err) {
      console.error('Error fetching AI settings:', err);
      setError(err.message || 'Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    // Clear success message when user makes changes
    setSuccess(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updatedSettings = await selectiveApi.admin.updateAISettings(settings);
      setSettings(updatedSettings);
      setSuccess('AI settings updated successfully!');

      // Refresh provider status
      const statusData = await selectiveApi.admin.getAIProviderStatus();
      setProviderStatus(statusData);
    } catch (err) {
      console.error('Error saving AI settings:', err);
      setError(err.message || 'Failed to save AI settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshStatus = async () => {
    try {
      const statusData = await selectiveApi.admin.getAIProviderStatus();
      setProviderStatus(statusData);
      setSuccess('Provider status refreshed');
    } catch (err) {
      console.error('Error refreshing status:', err);
      setError(err.message || 'Failed to refresh status');
    }
  };

  if (loading) {
    return (
      <div className="ai-config-panel loading">
        <div className="loading-spinner"></div>
        <p>Loading AI configuration...</p>
      </div>
    );
  }

  return (
    <div className="ai-config-panel">
      {/* Header */}
      <header className="config-header">
        <button onClick={onBack} className="back-button">
          ← Back to Dashboard
        </button>
        <div className="header-content">
          <h1>AI Configuration</h1>
          <p className="subtitle">Configure AI models for Selective Exam question generation</p>
        </div>
      </header>

      {/* Success/Error Messages */}
      {success && (
        <div className="alert alert-success">
          <Check size={16} className="inline-icon" /> {success}
        </div>
      )}
      {error && (
        <div className="alert alert-error">
          <TriangleAlert size={16} className="inline-icon" /> {error}
        </div>
      )}

      <div className="config-content">
        {/* Provider Status Card */}
        <section className="config-card provider-status">
          <div className="card-header">
            <h2>Provider Status</h2>
            <button
              onClick={handleRefreshStatus}
              className="refresh-button"
              title="Refresh status"
            >
              <RefreshCw size={16} className="inline-icon" /> Refresh
            </button>
          </div>

          <div className="status-grid">
            {/* Claude Status — Primary */}
            <div className={`provider-card ${providerStatus?.claude?.available ? 'available' : 'unavailable'}`}>
              <div className="provider-header">
                <span className="provider-name">Claude Sonnet 5 <span className="provider-role">(Primary)</span></span>
                <span className={`status-badge ${providerStatus?.claude?.available ? 'online' : 'offline'}`}>
                  {providerStatus?.claude?.available
                    ? <><CircleCheckBig size={14} className="inline-icon" /> Available</>
                    : <><CircleX size={14} className="inline-icon" /> Unavailable</>}
                </span>
              </div>
              <div className="provider-details">
                {providerStatus?.claude?.available ? (
                  <>
                    <p className="model-name">Model: {providerStatus.claude.model}</p>
                    <p className="provider-desc">Primary provider for all Selective Exam question generation</p>
                  </>
                ) : (
                  <p className="error-message">{providerStatus?.claude?.error || 'API key not configured'}</p>
                )}
              </div>
            </div>

            {/* Gemini Status — Fallback */}
            <div className={`provider-card ${providerStatus?.gemini?.available ? 'available' : 'unavailable'}`}>
              <div className="provider-header">
                <span className="provider-name">Google Gemini Pro <span className="provider-role">(Fallback)</span></span>
                <span className={`status-badge ${providerStatus?.gemini?.available ? 'online' : 'offline'}`}>
                  {providerStatus?.gemini?.available
                    ? <><CircleCheckBig size={14} className="inline-icon" /> Available</>
                    : <><CircleX size={14} className="inline-icon" /> Unavailable</>}
                </span>
              </div>
              <div className="provider-details">
                {providerStatus?.gemini?.available ? (
                  <>
                    <p className="model-name">Model: {providerStatus.gemini.model}</p>
                    <p className="provider-desc">Used automatically if Claude is unavailable or fails</p>
                  </>
                ) : (
                  <p className="error-message">{providerStatus?.gemini?.error || 'API key not configured'}</p>
                )}
              </div>
            </div>
          </div>

          {providerStatus?.preferred_provider && (
            <div className="preferred-info">
              <strong>Current Strategy:</strong> {providerStatus.preferred_provider.toUpperCase()}
              {providerStatus.preferred_provider === 'auto' && (
                <span className="strategy-note"> (Claude Sonnet 5 primary, Gemini Pro fallback)</span>
              )}
            </div>
          )}
        </section>

        {/* AI Provider Selection */}
        <section className="config-card">
          <h2>AI Provider Selection</h2>

          <div className="form-group">
            <label htmlFor="ai_provider">Primary AI Provider</label>
            <select
              id="ai_provider"
              value={settings.ai_provider}
              onChange={(e) => handleSettingChange('ai_provider', e.target.value)}
              className="form-control"
            >
              <option value="auto">AUTO (Claude primary, Gemini fallback)</option>
              <option value="claude" disabled={!providerStatus?.claude?.available}>
                Claude Sonnet 5 {!providerStatus?.claude?.available && '(Unavailable)'}
              </option>
              <option value="gemini" disabled={!providerStatus?.gemini?.available}>
                Gemini Pro {!providerStatus?.gemini?.available && '(Unavailable)'}
              </option>
            </select>
            <p className="field-help">
              AUTO uses Claude Sonnet 5 as the primary provider and automatically falls back to Gemini Pro if Claude is unavailable or fails.
            </p>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="gemini_model">Gemini Model</label>
              <input
                type="text"
                id="gemini_model"
                value={settings.gemini_model}
                onChange={(e) => handleSettingChange('gemini_model', e.target.value)}
                className="form-control"
                disabled={!providerStatus?.gemini?.available}
              />
            </div>

            <div className="form-group">
              <label htmlFor="claude_model">Claude Model</label>
              <input
                type="text"
                id="claude_model"
                value={settings.claude_model}
                onChange={(e) => handleSettingChange('claude_model', e.target.value)}
                className="form-control"
                disabled={!providerStatus?.claude?.available}
              />
            </div>
          </div>
        </section>

        {/* Quality Settings */}
        <section className="config-card">
          <h2>Quality Settings</h2>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.use_exemplars}
                onChange={(e) => handleSettingChange('use_exemplars', e.target.checked)}
              />
              <span>Enable Few-Shot Prompting</span>
            </label>
            <p className="field-help">
              Use past paper examples to improve question quality by 40-60%. Highly recommended.
            </p>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.enable_quality_validation}
                onChange={(e) => handleSettingChange('enable_quality_validation', e.target.checked)}
              />
              <span>Enable Quality Validation</span>
            </label>
            <p className="field-help">
              Validate distractor plausibility using semantic analysis.
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="min_distractor_quality">
              Minimum Distractor Quality: {settings.min_distractor_quality}
            </label>
            <input
              type="range"
              id="min_distractor_quality"
              min="0"
              max="1"
              step="0.1"
              value={settings.min_distractor_quality}
              onChange={(e) => handleSettingChange('min_distractor_quality', parseFloat(e.target.value))}
              className="slider"
            />
            <p className="field-help">
              Minimum quality threshold for distractor options (0-1 scale). Higher values ensure better quality.
            </p>
          </div>
        </section>

        {/* Advanced Settings */}
        <section className="config-card">
          <h2>Advanced Settings</h2>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.fallback_enabled}
                onChange={(e) => handleSettingChange('fallback_enabled', e.target.checked)}
              />
              <span>Enable Automatic Fallback</span>
            </label>
            <p className="field-help">
              Automatically switch to alternative provider if primary fails.
            </p>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="max_questions_per_request">Max Questions Per Request</label>
              <input
                type="number"
                id="max_questions_per_request"
                min="1"
                max="50"
                value={settings.max_questions_per_request}
                onChange={(e) => handleSettingChange('max_questions_per_request', parseInt(e.target.value))}
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="generation_timeout_seconds">Generation Timeout (seconds)</label>
              <input
                type="number"
                id="generation_timeout_seconds"
                min="30"
                max="300"
                value={settings.generation_timeout_seconds}
                onChange={(e) => handleSettingChange('generation_timeout_seconds', parseInt(e.target.value))}
                className="form-control"
              />
            </div>
          </div>
        </section>

        {/* Action Buttons */}
        <div className="actions">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          <button
            onClick={fetchSettings}
            disabled={saving}
            className="btn btn-secondary"
          >
            Reset to Current
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIConfigPanel;
